import { useContext, useState } from 'react';

import { filtersToQueryFilters, FilterValue, PillFilters } from 'features/common-elements';
import { LoanTable } from 'features/drilldown/LoanDatatable/LoanTable';
import {
  PortfolioFilterConfig,
  useFilterConfig,
} from 'features/pages/portfolio/Portfolio/portfolioFilters.config';
import { DownloadDocumentButton, SpinnerComponent } from 'ui-kit';

import { gql, useQuery } from '@apollo/client';

import { AssetClass, ParentType, SortableField, SortDirection } from '__generated__/globalTypes';

import { CardCommonProps, CardWrapper } from './CardWrapper';
import { CarveDropdown, CarveOption } from './CarveDropdown';
import { GetDealLoans, GetDealLoansVariables } from './gql/__generated__/GetDealLoans';
import { GET_DEAL_LOANS_QUERY } from './gql/inquiryQueries';
import { DataContext } from '../../../context/DataContext';
import { useFeatureFlag } from '../../../context/FeatureFlagContext';
import { useColumnOrder } from '../../../hooks/useColumnOrder';
import { getLoanTableConfigName } from '../../pages/portfolio/LoanLevelData';
import { CreDetailsModal } from '../../pages/portfolio/Portfolio/CreDetailsModal';
import {
  GetDealLoanSpreadsheetUrl,
  GetDealLoanSpreadsheetUrlVariables,
} from '../DealOverview/__generated__/GetDealLoanSpreadsheetUrl';
import { GET_DEAL_LOAN_SPREADSHEET_URL } from '../DealOverview/fragments';

const LoansCardFragments = {
  TimelineCard: gql`
    fragment LoansCard on TimelineCard {
      ... on LoansCard {
        assetClass: asset_class
        allow_filtering
      }
    }
  `,
};

const useLoansCard = (props: {
  dealId: string;
  assetClass: AssetClass;
  selectedCarve?: CarveOption | null;
}) => {
  const [filters, setFilters] = useState<FilterValue<PortfolioFilterConfig>[]>([]);
  const [sortings, setSortings] = useState<{ id: SortableField; desc: boolean }[]>([]);

  const gqlAssetClass = props.assetClass;
  const portfolioFiltersConfig = useFilterConfig(gqlAssetClass, {
    parentType: ParentType.DEAL,
    parentId: props.dealId,
  });
  const queryFilters = portfolioFiltersConfig
    ? filtersToQueryFilters<PortfolioFilterConfig>(filters, portfolioFiltersConfig)
    : [];

  const carveQueryFilters = props.selectedCarve?.carveFilters || [];

  const { data, previousData, loading } = useQuery<GetDealLoans, GetDealLoansVariables>(
    GET_DEAL_LOANS_QUERY,
    {
      variables: {
        id: props.dealId,
        sort: {
          sort_direction: sortings[0]?.desc ? SortDirection.desc : SortDirection.asc,
          sort_field: sortings[0]?.id,
        },
        pagination: {},
        // Directly pass the carve filters like in the diligence code
        filters: [...queryFilters, ...carveQueryFilters],
      },
      //TODO: understand why it doesn't work without this
      fetchPolicy: 'no-cache',
    },
  );

  // fix reloading issue: https://orsnn.atlassian.net/browse/ORSNN-953
  // loan_spreadsheet_url will be queried separately so it will update the Deal cache, but the next getDeal query will
  // not get a new loans_spreadsheet_url so it won't re-update the Deal cache
  const { data: dealDataLoanSpreadsheetUrl } = useQuery<
    GetDealLoanSpreadsheetUrl,
    GetDealLoanSpreadsheetUrlVariables
  >(GET_DEAL_LOAN_SPREADSHEET_URL, {
    variables: {
      id: props.dealId,
    },
  });
  const downloadUrl = dealDataLoanSpreadsheetUrl?.deal?.loans_spreadsheet_url;

  const dataToUse = data || previousData;
  const loans = dataToUse?.deal?.loans;
  return {
    filters,
    loans,
    gqlAssetClass,
    portfolioFiltersConfig,
    setFilters,
    downloadUrl,
    loading,
    sortings,
    setSortings,
  };
};

type LoansCardProps = {
  dealId: string;
  role: string;
  allow_filtering?: boolean;
} & CardCommonProps;

function LoansCard(props: LoansCardProps) {
  return (
    <CardWrapper {...props} name="loans">
      {props.assetClass != null ? <LoansCardBody {...props} assetClass={props.assetClass} /> : null}
    </CardWrapper>
  );
}

const LoansCardBody = (props: {
  dealId: string;
  assetClass: AssetClass;
  role: string;
  allow_filtering?: boolean;
}) => {
  const [selectedCarve, setSelectedCarve] = useState<CarveOption | null>(null);

  const {
    sortings,
    setSortings,
    downloadUrl,
    filters,
    loans,
    gqlAssetClass,
    portfolioFiltersConfig,
    setFilters,
    loading,
  } = useLoansCard({ ...props, selectedCarve });

  const { role } = props;
  const isCREEnabled = useFeatureFlag('creEnabled');
  const { state } = useContext(DataContext);

  const { setColumnOrder } = useColumnOrder(
    `loanTable_${getLoanTableConfigName(gqlAssetClass)}`,
    state.globalTableOrderConfig?.myPortfolio?.loanTable[
      getLoanTableConfigName(gqlAssetClass)
    ] as string[],
  );

  const [creModalLoanId, setCreModalLoanId] = useState<string>('');
  const [isOpenCREModal, setIsOpenCREModal] = useState<boolean>(false);

  const handleCREClick = (loanId: string) => {
    setIsOpenCREModal(true);
    setCreModalLoanId(loanId);
  };

  const handleCloseCREModal = () => {
    setIsOpenCREModal(false);
  };

  const tableBody =
    loans && gqlAssetClass && portfolioFiltersConfig ? (
      <>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <CarveDropdown
              dealId={props.dealId}
              value={selectedCarve?.id}
              onValueChange={setSelectedCarve}
              className="min-w-[200px]"
              allowFiltering={props.allow_filtering}
            />
            <PillFilters
              filtersConfig={portfolioFiltersConfig}
              currentFilters={filters}
              setCurrentFilters={setFilters}
            />
          </div>
          {downloadUrl && (
            <DownloadDocumentButton
              downloadUrl={downloadUrl}
              fileType="excel"
              tooltipText="Download Excel"
              tooltipPosition="left"
              className="mr-4"
            />
          )}
        </div>
        <div style={{ height: '400px' }}>
          <LoanTable
            data={loans ?? []}
            assetClass={gqlAssetClass}
            strings={{ empty: 'No Loans Found' }}
            sorting={{ state: sortings, onSortingChanged: setSortings }}
            withListingLink={false}
            setColumnOrder={setColumnOrder}
            handleCREClick={isCREEnabled ? handleCREClick : undefined}
            isOnlyInfoIcon={isCREEnabled}
          />
        </div>
        {isOpenCREModal && (
          <CreDetailsModal
            handleCloseCREModal={handleCloseCREModal}
            creModalLoanId={creModalLoanId}
            role={role}
            isPortfolio={false}
          />
        )}
      </>
    ) : null;

  return (
    <>
      {tableBody}
      <SpinnerComponent loading={loading} positionRelative={!loans} />
    </>
  );
};

export { LoansCard, LoansCardBody, LoansCardFragments };
